import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import BrandName from '../components/BrandName';
import api from '../services/api';

class AcceptInvite extends Component {
  constructor(props) {
    super(props);
    this.state = {
      token: '',
      email: '',
      inviterName: '',
      isAdmin: false,
      firstName: '',
      lastName: '',
      company: '',
      password: '',
      confirmPassword: '',
      errors: {},
      isValidating: true,
      isValid: false,
      isSubmitting: false,
      validationError: null,
      submitError: null,
    };
  }

  componentDidMount() {
    // Get token from URL
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (!token) {
      this.setState({
        isValidating: false,
        isValid: false,
        validationError: 'Invalid invitation link',
      });
      return;
    }

    this.setState({ token }, () => {
      this.validateToken(token);
    });
  }

  validateToken = async (token) => {
    try {
      const response = await api.get(`/invitations/validate/${token}`);

      if (response.data.valid) {
        this.setState({
          isValidating: false,
          isValid: true,
          email: response.data.email,
          inviterName: response.data.inviterName,
          isAdmin: response.data.admin,
        });
      } else {
        this.setState({
          isValidating: false,
          isValid: false,
          validationError: response.data.message || 'Invalid invitation',
        });
      }
    } catch (error) {
      this.setState({
        isValidating: false,
        isValid: false,
        validationError: error.response?.data?.message || 'Invalid or expired invitation',
      });
    }
  };

  handleChange = (e) => {
    const { name, value } = e.target;
    this.setState({
      [name]: value,
      errors: { ...this.state.errors, [name]: '' },
    });
  };

  handleKeyPress = (e) => {
    if (e.key === 'Enter' && !this.state.isSubmitting) {
      e.preventDefault();
      this.handleSubmit();
    }
  };

  validateForm = () => {
    const { firstName, lastName, password, confirmPassword } = this.state;
    const errors = {};

    if (!firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }

    if (!lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }

    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      errors.password = 'Password must contain uppercase, lowercase, and number';
    }

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
  };

  handleSubmit = async () => {
    const errors = this.validateForm();
    if (Object.keys(errors).length > 0) {
      this.setState({ errors });
      return;
    }

    this.setState({ isSubmitting: true, submitError: null });

    const { token, firstName, lastName, company, password } = this.state;

    try {
      const response = await api.post('/invitations/accept', {
        token,
        firstName,
        lastName,
        company,
        password,
      });

      // Save token and user to localStorage
      const { token: authToken, user } = response.data;
      localStorage.setItem('token', authToken);
      localStorage.setItem('user', JSON.stringify(user));

      // Redirect to dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      this.setState({
        isSubmitting: false,
        submitError: error.response?.data?.message || 'Failed to accept invitation',
      });
    }
  };

  render() {
    const {
      email,
      inviterName,
      isAdmin,
      firstName,
      lastName,
      company,
      password,
      confirmPassword,
      errors,
      isValidating,
      isValid,
      isSubmitting,
      validationError,
      submitError,
    } = this.state;

    // Loading state
    if (isValidating) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="card text-center">
              <LoadingSpinner size="large" />
              <p className="mt-4 text-slate-600">Validating invitation...</p>
            </div>
          </div>
        </div>
      );
    }

    // Invalid invitation
    if (!isValid) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="card animate-fade-in">
              <div className="flex justify-center mb-8">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>

              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-slate-800 mb-2">Invalid Invitation</h1>
                <p className="text-slate-600 mb-4">{validationError}</p>
                <p className="text-sm text-slate-500">
                  This invitation may have expired or already been used.
                </p>
              </div>

              <Link to="/login" className="btn-primary w-full block text-center">
                Go to Login
              </Link>
            </div>
          </div>
        </div>
      );
    }

    // Valid invitation - show signup form
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="card animate-fade-in">
            {/* Logo */}
            <div className="flex justify-center mb-6 sm:mb-8">
              <img
                src="/logo.png"
                alt="PlansSource Logo"
                className="w-20 h-20 sm:w-32 sm:h-32 object-contain"
              />
            </div>

            {/* Header */}
            <div className="text-center mb-6 sm:mb-8">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">
                You're Invited!
              </h1>
              <p className="text-sm sm:text-base text-slate-600">
                <strong>{inviterName}</strong> invited you to join <BrandName />
                {isAdmin && (
                  <span className="block mt-1">
                    <span className="inline-block px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                      Admin Access
                    </span>
                  </span>
                )}
              </p>
            </div>

            {/* Error Alert */}
            {submitError && (
              <div className="mb-4 sm:mb-6 bg-red-50 border border-red-200 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded-lg text-xs sm:text-sm">
                {submitError}
              </div>
            )}

            {/* Signup Form */}
            <form
              className="space-y-3 sm:space-y-4"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                return false;
              }}
            >
              {/* Email (Read-only) */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={email}
                  readOnly
                  className="input-field bg-slate-50 cursor-not-allowed"
                />
              </div>

              {/* Name Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-slate-700 mb-2">
                    First name
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={this.handleChange}
                    onKeyPress={this.handleKeyPress}
                    className={`input-field ${errors.firstName ? 'border-red-500' : ''}`}
                    placeholder="John"
                    disabled={isSubmitting}
                    autoFocus
                  />
                  {errors.firstName && <p className="error-text">{errors.firstName}</p>}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-slate-700 mb-2">
                    Last name
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={this.handleChange}
                    onKeyPress={this.handleKeyPress}
                    className={`input-field ${errors.lastName ? 'border-red-500' : ''}`}
                    placeholder="Doe"
                    disabled={isSubmitting}
                  />
                  {errors.lastName && <p className="error-text">{errors.lastName}</p>}
                </div>
              </div>

              {/* Company Field */}
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                  Company <span className="text-slate-400">(optional)</span>
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  autoComplete="organization"
                  value={company}
                  onChange={this.handleChange}
                  onKeyPress={this.handleKeyPress}
                  className="input-field"
                  placeholder="Your Company Inc"
                  disabled={isSubmitting}
                />
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-2">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={this.handleChange}
                  onKeyPress={this.handleKeyPress}
                  className={`input-field ${errors.password ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                  disabled={isSubmitting}
                />
                {errors.password && <p className="error-text">{errors.password}</p>}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-2">
                  Confirm password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={this.handleChange}
                  onKeyPress={this.handleKeyPress}
                  className={`input-field ${errors.confirmPassword ? 'border-red-500' : ''}`}
                  placeholder="••••••••"
                  disabled={isSubmitting}
                />
                {errors.confirmPassword && <p className="error-text">{errors.confirmPassword}</p>}
              </div>

              {/* Submit Button */}
              <button
                type="button"
                disabled={isSubmitting}
                className="btn-primary w-full min-h-[44px]"
                onClick={this.handleSubmit}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <LoadingSpinner size="small" />
                    <span className="ml-2">Creating account...</span>
                  </span>
                ) : (
                  'Accept Invitation & Create Account'
                )}
              </button>
            </form>

            {/* Login Link */}
            <p className="mt-6 sm:mt-8 text-center text-sm text-slate-600">
              Already have an account?{' '}
              <Link to="/login" className="text-teal-600 hover:text-teal-700 font-semibold">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    );
  }
}

export default AcceptInvite;